import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { InitialEnquiryModalValues } from '../types';
import type { ISearchPageAds } from '../api/types/searchPageApiTypes';
import { DealerBusinessTypes } from '../api/types/adviewApiTypes';
import getConfig from 'next/config';

interface AdDetailsProviderProps {
  children: ReactNode;
  userSaved?: boolean;
  section?: string;
}

type category = {
  id: number;
  description: string;
};

interface AdDetailsContextI {
  isReportAdModalOpen: boolean;
  setIsReportAdModalOpen: (t: boolean) => void;
  adId: number;
  setAdId: (t: number) => void;
  categories?: category[];
  setCategories: (t: category[]) => void;
  currentReportAdView: 'INITIAL' | 'SUCCESS';
  setCurrentReportAdView: (t: 'INITIAL' | 'SUCCESS') => void;
  isSendMessageModalOpen: boolean;
  setIsSendMessageModalOpen: (t: boolean) => void;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (t: boolean) => void;
  isAdSaved: boolean;
  setIsAdSaved: (t: boolean) => void;
  isDealerEnquiryModalOpen: boolean;
  setIsDealerEnquiryModalOpen: (t: boolean) => void;
  initialEnquiryValues?: InitialEnquiryModalValues;
  setInitialEnquiryValues: (t: InitialEnquiryModalValues) => void;
  isYoutubeReviewOpen: boolean;
  setIsYoutubeReviewOpen: (t: boolean) => void;
  similarAds: ISearchPageAds[];
  setSimilarAds: (recommendedAds: ISearchPageAds[]) => void;
  isPostEnquirySimilarAdsModalOpen: boolean;
  setIsPostEnquirySimilarAdsModalOpen: (t: boolean) => void;
  isPricePerMonthModalOpen: boolean;
  setIsPricePerMonthModalOpen: (t: boolean) => void;
  isTrustedDealerModalOpen: boolean;
  setTrustedDealerModalOpen: (t: boolean) => void;
  isContactSellerModalOpen: boolean;
  setIsContactSellerModalOpen: (isOpen: boolean) => void;
  isReservationModalOpen: boolean;
  setIsReservationModalOpen: (isOpen: boolean) => void;
  isDealerBusinessTypeModalOpen: boolean;
  dealerBusinessTypeModalName: DealerBusinessTypes | null;
  setDealerBusinessTypeModalName: (name: DealerBusinessTypes | null) => void;
  enableLoggedOutDealerEnquiry?: boolean;
  isPricingInsightsModalOpen: boolean;
  setIsPricingInsightsModalOpen: (isOpen: boolean) => void;
}

const AdDetailsContext = createContext<AdDetailsContextI>({
  isReportAdModalOpen: false,
  setIsReportAdModalOpen: () => null,
  adId: 0,
  setAdId: () => null,
  categories: [],
  setCategories: () => null,
  currentReportAdView: 'INITIAL',
  setCurrentReportAdView: () => null,
  isSendMessageModalOpen: false,
  setIsSendMessageModalOpen: () => null,
  isShareModalOpen: false,
  setIsShareModalOpen: () => null,
  isAdSaved: false,
  setIsAdSaved: () => null,
  isDealerEnquiryModalOpen: false,
  setIsDealerEnquiryModalOpen: () => null,
  initialEnquiryValues: undefined,
  setInitialEnquiryValues: () => null,
  isYoutubeReviewOpen: false,
  setIsYoutubeReviewOpen: () => null,
  similarAds: [],
  setSimilarAds: () => null,
  isPostEnquirySimilarAdsModalOpen: false,
  setIsPostEnquirySimilarAdsModalOpen: () => null,
  isPricePerMonthModalOpen: false,
  setIsPricePerMonthModalOpen: () => null,
  isTrustedDealerModalOpen: false,
  setTrustedDealerModalOpen: () => null,
  isContactSellerModalOpen: false,
  setIsContactSellerModalOpen: () => null,
  isReservationModalOpen: false,
  setIsReservationModalOpen: () => null,
  isDealerBusinessTypeModalOpen: false,
  dealerBusinessTypeModalName: null,
  setDealerBusinessTypeModalName: () => null,
  enableLoggedOutDealerEnquiry: false,
  isPricingInsightsModalOpen: false,
  setIsPricingInsightsModalOpen: () => null,
});

const AdDetailsProvider = (props: AdDetailsProviderProps) => {
  const { children, userSaved } = props;
  const [isReportAdModalOpen, setIsReportAdModalOpen] =
    useState<boolean>(false);
  const [isTrustedDealerModalOpen, setTrustedDealerModalOpen] =
    useState<boolean>(false);
  const [adId, setAdId] = useState<number>(0);
  const [categories, setCategories] = useState<category[]>();
  const [currentReportAdView, setCurrentReportAdView] = useState<
    'INITIAL' | 'SUCCESS'
  >('INITIAL');

  const [isSendMessageModalOpen, setIsSendMessageModalOpen] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isAdSaved, setIsAdSaved] = useState<boolean>(userSaved ?? false);
  const [isDealerEnquiryModalOpen, setIsDealerEnquiryModalOpen] =
    useState<boolean>(false);
  const [initialEnquiryValues, setInitialEnquiryValues] = useState<
    InitialEnquiryModalValues | undefined
  >(undefined);
  const [
    isPostEnquirySimilarAdsModalOpen,
    setIsPostEnquirySimilarAdsModalOpen,
  ] = useState<boolean>(false);
  const [isYoutubeReviewOpen, setIsYoutubeReviewOpen] =
    useState<boolean>(false);
  const [similarAds, setSimilarAds] = useState<ISearchPageAds[]>([]);
  const [isPricePerMonthModalOpen, setIsPricePerMonthModalOpen] =
    useState<boolean>(false);
  const [isContactSellerModalOpen, setIsContactSellerModalOpen] =
    useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [dealerBusinessTypeModalName, setDealerBusinessTypeModalName] =
    useState<DealerBusinessTypes | null>(null);
  const isDealerBusinessTypeModalOpen = Boolean(dealerBusinessTypeModalName);
  const [isPricingInsightsModalOpen, setIsPricingInsightsModalOpen] =
    useState<boolean>(false);

  const {
    publicRuntimeConfig: { ENABLE_LOGGED_OUT_DEALER_ENQUIRY_MODAL },
  } = getConfig();

  const enableLoggedOutDealerEnquiry =
    ENABLE_LOGGED_OUT_DEALER_ENQUIRY_MODAL && props.section === 'cars';

  const memoizedContextValue = useMemo(
    () => ({
      isReportAdModalOpen,
      setIsReportAdModalOpen,
      adId,
      setAdId,
      categories,
      setCategories,
      currentReportAdView,
      setCurrentReportAdView,
      isSendMessageModalOpen,
      setIsSendMessageModalOpen,
      isShareModalOpen,
      setIsShareModalOpen,
      isAdSaved,
      setIsAdSaved,
      isDealerEnquiryModalOpen,
      setIsDealerEnquiryModalOpen,
      initialEnquiryValues,
      setInitialEnquiryValues,
      isYoutubeReviewOpen,
      setIsYoutubeReviewOpen,
      similarAds,
      setSimilarAds,
      isPostEnquirySimilarAdsModalOpen,
      setIsPostEnquirySimilarAdsModalOpen,
      isPricePerMonthModalOpen,
      setIsPricePerMonthModalOpen,
      isTrustedDealerModalOpen,
      setTrustedDealerModalOpen,
      isContactSellerModalOpen,
      setIsContactSellerModalOpen,
      isReservationModalOpen,
      setIsReservationModalOpen,
      isDealerBusinessTypeModalOpen,
      dealerBusinessTypeModalName,
      setDealerBusinessTypeModalName,
      enableLoggedOutDealerEnquiry,
      isPricingInsightsModalOpen,
      setIsPricingInsightsModalOpen,
    }),
    [
      isReportAdModalOpen,
      setIsReportAdModalOpen,
      adId,
      setAdId,
      categories,
      setCategories,
      currentReportAdView,
      setCurrentReportAdView,
      isSendMessageModalOpen,
      setIsSendMessageModalOpen,
      isShareModalOpen,
      setIsShareModalOpen,
      isAdSaved,
      setIsAdSaved,
      isDealerEnquiryModalOpen,
      setIsDealerEnquiryModalOpen,
      initialEnquiryValues,
      setInitialEnquiryValues,
      isYoutubeReviewOpen,
      setIsYoutubeReviewOpen,
      similarAds,
      setSimilarAds,
      isPostEnquirySimilarAdsModalOpen,
      setIsPostEnquirySimilarAdsModalOpen,
      isPricePerMonthModalOpen,
      setIsPricePerMonthModalOpen,
      isTrustedDealerModalOpen,
      setTrustedDealerModalOpen,
      isContactSellerModalOpen,
      setIsContactSellerModalOpen,
      isReservationModalOpen,
      setIsReservationModalOpen,
      isDealerBusinessTypeModalOpen,
      dealerBusinessTypeModalName,
      setDealerBusinessTypeModalName,
      enableLoggedOutDealerEnquiry,
      isPricingInsightsModalOpen,
      setIsPricingInsightsModalOpen,
    ],
  );

  return (
    <AdDetailsContext.Provider value={memoizedContextValue}>
      {children}
    </AdDetailsContext.Provider>
  );
};
const useAdDetailsContext = () => useContext(AdDetailsContext);

export { AdDetailsProvider, useAdDetailsContext };
